import React from "react";
import { Helmet } from "react-helmet";
import { PageShell } from "./ui/PageShell";
import { Eyebrow } from "./ui/Eyebrow";
import { Reveal } from "./ui/Reveal";

const videos = [
  {
    id: 1,
    title: "Tour of Advanced Automotive Smog Shop!",
    description:
      "Take a tour of the shop at Advanced Automotive Smog and learn some fun facts!",
    thumbnail: "https://img.youtube.com/vi/aRjAKPJAHqw/maxresdefault.jpg",
    url: "https://www.youtube.com/watch?v=aRjAKPJAHqw",
  },
  {
    id: 2,
    title: "Checking the Oil.",
    description: "Learn how to check your oil and get some pro tips!",
    thumbnail: "https://img.youtube.com/vi/HvmH8AMTef4/maxresdefault.jpg",
    url: "https://www.youtube.com/watch?v=HvmH8AMTef4",
  },
  {
    id: 3,
    title: "Torque The Tire!",
    description:
      "Learn how to properly torque your tires with a torque wrench, right here at Advanced Automotive Smog!",
    thumbnail: "https://img.youtube.com/vi/tsht3WoTZ98/maxresdefault.jpg",
    url: "https://www.youtube.com/watch?v=tsht3WoTZ98",
  },
  {
    id: 4,
    title: "Washer Fluid Maintenance Made Easy",
    description:
      "Learn how to check your washer fluid here at Advanced Automotive Smog!",
    thumbnail: "https://img.youtube.com/vi/MBg2_AhBizM/maxresdefault.jpg",
    url: "https://www.youtube.com/watch?v=MBg2_AhBizM",
  },
];

const cardLinkClass =
  "group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-teal-600 hover:shadow-lg no-underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2";

function PlayBadge({ size = "sm" }) {
  const lg = size === "lg";
  return (
    <div
      className={
        lg
          ? "flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white/95 shadow-md backdrop-blur-sm"
          : "flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white/95 shadow-md backdrop-blur-sm"
      }
      aria-hidden
    >
      <div
        className={
          lg
            ? "ml-0.5 h-0 w-0 border-t-[9px] border-b-[9px] border-l-[15px] border-t-transparent border-b-transparent border-l-teal-600"
            : "ml-0.5 h-0 w-0 border-t-[8px] border-b-[8px] border-l-[13px] border-t-transparent border-b-transparent border-l-teal-600"
        }
      />
    </div>
  );
}

const Videos = () => {
  return (
    <>
      {/* SEO Meta Tags with Helmet */}
      <Helmet>
        <title>
          Vehicle Maintenance Videos | Advanced Automotive Smog
        </title>

        <meta
          name="description"
          content="Short videos from our Temecula shop covering basic vehicle maintenance, from checking your oil to torquing tires—practical tips you can watch in minutes."
        />
        <meta
          name="keywords"
          content="car maintenance videos, vehicle maintenance tips, how to check oil, tire torque, washer fluid maintenance, Temecula auto shop videos"
        />

        {/* Open Graph Metadata (Facebook, LinkedIn) */}
        <meta
          property="og:title"
          content="Vehicle Maintenance Videos | Advanced Automotive Smog"
        />
        <meta
          property="og:description"
          content="Short videos from our Temecula shop covering basic vehicle maintenance, from checking your oil to torquing tires—practical tips you can watch in minutes."
        />
        <meta
          property="og:image"
          content="https://www.advancedautomotivesmog.com/assets/blogs.jpeg"
        />
        <meta
          property="og:url"
          content="https://www.advancedautomotivesmog.com/blogs"
        />
        <meta property="og:type" content="website" />

        {/* Twitter Metadata */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vehicle Maintenance Videos | Advanced Automotive Smog"
        />
        <meta
          name="twitter:description"
          content="Short videos from our Temecula shop covering basic vehicle maintenance—practical tips you can watch in minutes."
        />
        <meta
          name="twitter:image"
          content="https://www.advancedautomotivesmog.com/assets/blogs.jpeg"
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href="https://www.advancedautomotivesmog.com/blogs"
        />
      </Helmet>

      <PageShell className="bg-[#F8F9FA] text-slate-700">
        {/* ===== MOBILE LAYOUT ===== */}
        <div className="border-b border-slate-200 bg-[#EEF2F7] md:hidden">
        <div className="mx-auto max-w-xl border-l-4 border-slate-300 px-4 pb-8 pt-7 sm:px-5">
          <div className="mb-8">
            <Eyebrow tone="light" variant="compact" className="mb-3">
              Video Library
            </Eyebrow>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-2xl">
              Watch{" "}
              <span className="text-teal-600">
                real tips from our Temecula shop
              </span>
              .
            </h1>
            <p className="text-[0.8rem] text-slate-500">
              Short, practical videos to help you understand smog checks, basic
              maintenance, and what we do here at Advanced Automotive Smog.
            </p>
          </div>
        </div>
        </div>

        <div className="border-t border-slate-200 bg-white px-4 py-10 pb-14 sm:px-5 md:hidden">
          <div className="mx-auto max-w-xl space-y-5">
            {videos.map((video) => (
              <a
                key={video.id}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cardLinkClass}
              >
                <div className="relative h-48 w-full overflow-hidden bg-slate-100">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                    style={{ backgroundImage: `url(${video.thumbnail})` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayBadge size="sm" />
                  </div>
                </div>

                <div className="p-3.5 sm:p-4">
                  <h2 className="mb-2 text-sm font-semibold text-navy-900">
                    {video.title}
                  </h2>
                  <p className="mb-3 text-[0.8rem] text-slate-700">
                    {video.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[0.75rem] font-medium text-teal-600 transition-colors duration-300 group-hover:text-teal-700">
                    Watch on YouTube
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="mx-auto mt-8 max-w-xl text-center">
            <p className="text-[0.75rem] text-slate-500">
              Find more shop tours and maintenance tips on our YouTube channel—subscribe to see new uploads.
            </p>
          </div>
        </div>

        {/* ===== DESKTOP / TABLET LAYOUT ===== */}
        <Reveal className="mx-auto hidden md:block">
          <div className="border-b border-slate-200 bg-[#EEF2F7] px-8 pb-12 pt-8">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <Eyebrow tone="light" className="mb-4">
                Advanced Automotive · Videos
              </Eyebrow>
              <h1 className="mb-4 text-3xl font-semibold leading-snug tracking-tight text-navy-900 md:text-4xl">
                Learn what happens in the{" "}
                <span className="text-teal-600">bay and under the hood</span>.
              </h1>
              <p className="text-sm text-slate-500 md:text-base">
                Quick walkthroughs and how-tos filmed right here at Advanced
                Automotive Smog. See how we care for your vehicle before you even
                step into the shop.
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-[#F8F9FA] px-8 pb-16 pt-10">
            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-2">
              {videos.map((video) => (
                <a
                  key={video.id}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardLinkClass}
                >
                  <div className="relative h-56 w-full overflow-hidden bg-slate-100">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                      style={{ backgroundImage: `url(${video.thumbnail})` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PlayBadge size="lg" />
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col justify-between p-4 sm:p-5">
                    <div>
                      <h2 className="mb-2 text-lg font-semibold text-navy-900">
                        {video.title}
                      </h2>
                      <p className="mb-3 text-sm text-slate-700">
                        {video.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-between text-[0.85rem] text-teal-600">
                      <span className="font-medium transition-colors duration-300 group-hover:text-teal-700">
                        Watch on YouTube
                      </span>
                      <span className="text-sm text-slate-500 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-teal-600 motion-reduce:group-hover:translate-x-0">
                        ›
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mx-auto mt-10 max-w-6xl text-center">
              <p className="text-sm text-slate-500">
                Have a maintenance or emissions topic you&apos;d like to see covered? Mention it the next time you&apos;re in the shop.
              </p>
            </div>
          </div>
        </Reveal>
      </PageShell>
    </>
  );
};

export default Videos;
